import { useEffect, useRef, useState } from 'react';

/**
 * A text field that persists when you LEAVE it, not on every keystroke.
 *
 * The bid cover fields used to call `updateBid` from `onChange`, so a 40-character description was
 * 40 `PUT /bids/<id>` requests — and, until the store stopped merging the server echo, a race that
 * ate characters and made spaces impossible to type at all (see `bidStore.updateBid`).
 *
 * Commit semantics follow `MatterInfo`'s edit-in-place fields, the closest precedent in the repo:
 * blur saves, Enter saves, Escape reverts, and an unchanged value writes NOTHING — a blur that
 * changed nothing must not queue a request.
 */
interface BlurCommitInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur'> {
  value: string;
  /** Called only when the field is left AND the value actually changed. */
  onCommit: (next: string) => void;
  /** Render a <textarea> instead (same semantics). */
  multiline?: boolean;
}

export const BlurCommitInput = ({
  value,
  onCommit,
  multiline = false,
  ...rest
}: BlurCommitInputProps): JSX.Element => {
  const [draft, setDraft] = useState(value);
  /** True between the first keystroke and the commit — guards the two effects below. */
  const editing = useRef(false);

  // Read through a ref so the unmount cleanup below sees the CURRENT draft rather than the one
  // captured when the component mounted.
  const latest = useRef({ draft, value, onCommit });
  latest.current = { draft, value, onCommit };

  // Adopt an external change (another tab, a refresh, a server echo) only while the user is NOT
  // mid-edit. Adopting unconditionally is the same clobber the store had, moved one layer up.
  useEffect(() => {
    if (!editing.current) setDraft(value);
  }, [value]);

  const commit = (): void => {
    if (!editing.current) return;
    editing.current = false;
    const current = latest.current;
    // Unchanged means no request. Tabbing THROUGH a field must not save it.
    if (current.draft !== current.value) current.onCommit(current.draft);
  };

  // Navigating away can unmount without ever firing blur (a route change, closing the tab strip),
  // which would silently discard what was typed. Flush instead.
  useEffect(() => () => commit(), []);   // eslint-disable-line react-hooks/exhaustive-deps

  const handlers = {
    value: draft,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      editing.current = true;
      setDraft(e.target.value);
    },
    onBlur: commit,
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !multiline) {
        commit();
        (e.target as HTMLElement).blur();
      }
      if (e.key === 'Escape') {
        editing.current = false;
        setDraft(latest.current.value);
      }
    },
  };

  return multiline
    ? <textarea {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)} {...handlers} />
    : <input {...rest} {...handlers} />;
};

export default BlurCommitInput;
